import { useQueryClient } from '@tanstack/react-query';
import { TypeCreateWorkApplicationsRequest, TypeCreateWorkApplicationsResponse } from '~/api/workApplications';
import { type MockResponseStep, registerMockAPI } from '~/mocks/mockApis';

import AdditionalSuggestionBottomSheet from '~/components/WorkApply/BottomSheet/AdditionalSuggestionBottomSheet/AdditionalSuggestionBottomSheet';

import { RECRUITMENT_NOT_AVAILABLE_ERROR_CODE } from '~/constants/api';
import { type AdditionalSubmitResult } from '~/hooks/api/useSubmitAdditionalWorkApply';
import { useDedupeMutation } from '~/hooks/common/useDedupeMutation';
import bottomSheet from '~/services/bottomSheetService';
import { axiosInstance } from '~/services/fetchServiceV2';
import { formatDate } from '~/utils';
import { type TypeErrorFormat, defaultErrorHandler } from '~/utils/fetchHelper';

import { fetchRecommendations } from './useGetRecommendations';

type SubmitFlowCallbacks = {
  onSuccess: () => void /** 지원 성공 (추가 지원 포함) */;
  onRecruitmentClosed: (dateText: string) => void /** 최초 지원 모집 마감 */;
  onAdditionalAllClosed: (closedDates: string) => void /** 추가 지원 전체 마감 */;
  onAdditionalPartialClosed: (closedDates: string) => void /** 추가 지원 일부 마감 */;
};

/**
 * @description 근무 지원 제출 플로우 훅 (최초 지원)
 */
export const useSubmitWorkApply = () => {
  const queryClient = useQueryClient();

  const [trigger, { isPending }] = useDedupeMutation<
    TypeCreateWorkApplicationsRequest,
    TypeCreateWorkApplicationsResponse
  >({
    mutationFn: (params) => axiosInstance.post('/work-applications', { ...params, privacyPolicyAgreed: true }),
  });

  const requestSubmitWorkApply = (params: TypeCreateWorkApplicationsRequest, callbacks: SubmitFlowCallbacks) => {
    trigger({
      params,
      onSuccess: async ({ data }: TypeCreateWorkApplicationsResponse) => {
        const workApplicationId = data?.tid;

        if (!workApplicationId) {
          callbacks.onSuccess();
          return;
        }
        /** 추천 공고 조회 및 추가 지원 플로우 */
        try {
          const recommendationData = await fetchRecommendations(queryClient, workApplicationId);

          if (!recommendationData || recommendationData.recommendations.length === 0) {
            callbacks.onSuccess();
            return;
          }

          const result = await bottomSheet.open<AdditionalSubmitResult>(({ open, onClose }) => (
            <AdditionalSuggestionBottomSheet
              open={open}
              onClose={onClose}
              workApplicationId={workApplicationId}
              params={params}
            />
          ));

          switch (result?.type) {
            case 'allClosed':
              callbacks.onAdditionalAllClosed(result.closedDates);
              break;
            case 'partialClosed':
              callbacks.onAdditionalPartialClosed(result.closedDates);
              break;
            case 'error':
              break;
            default:
              callbacks.onSuccess();
              break;
          }
        } catch {
          // 추천 조회 실패해도 최초 지원은 성공
          callbacks.onSuccess();
        }
      },
      onError: (error: TypeErrorFormat) => {
        if (RECRUITMENT_NOT_AVAILABLE_ERROR_CODE === error.code) {
          callbacks.onRecruitmentClosed(formatDate({ value: params.workDate, format: 'M월 D일' }));
          return;
        }
        defaultErrorHandler(error);
      },
    });
  };

  return [requestSubmitWorkApply, { isPending }] as const;
};

registerMockAPI<TypeCreateWorkApplicationsResponse>({
  method: 'post',
  endpoint: '/work-applications',
  label: '지원 정보 확인/지원',
  scenarios: [
    {
      name: '지원 성공',
      response: { code: '0000', message: '정상', data: undefined } satisfies TypeCreateWorkApplicationsResponse,
    },
    {
      name: '지원 성공 (추천 조회 가능)',
      response: {
        code: '0000',
        message: '정상',
        data: { tid: 'mock-tid-001' },
      } satisfies TypeCreateWorkApplicationsResponse,
    },
    {
      name: '지원 마감 (400)',
      response: {
        code: 'RN002',
        message: '모집중인 공고가 아닙니다.',
        data: { tid: '' },
      } satisfies TypeCreateWorkApplicationsResponse,
      status: 400,
    },
    {
      name: '서버 에러 (500)',
      response: {
        code: '500',
        message: '서버 내부 오류가 발생했습니다.',
        data: { tid: '' },
      } satisfies TypeCreateWorkApplicationsResponse,
      status: 500,
    },
    {
      name: '추가 지원 일부 마감 (날짜 3개 선택 시 동작)',
      responses: [
        { response: { code: '0000', message: '정상', data: { tid: 'mock-tid-001' } } },
        { response: { code: '0000', message: '정상', data: undefined } },
        { response: { code: 'RN002', message: '모집중인 공고가 아닙니다.', data: { tid: '' } }, status: 400 },
        { response: { code: 'RN002', message: '모집중인 공고가 아닙니다.', data: { tid: '' } }, status: 400 },
      ] satisfies MockResponseStep<TypeCreateWorkApplicationsResponse>[],
    },
    {
      name: '추가 지원 전체 마감 (날짜 3개 선택 시 동작)',
      responses: [
        { response: { code: '0000', message: '정상', data: { tid: 'mock-tid-001' } } },
        { response: { code: 'RN002', message: '모집중인 공고가 아닙니다.', data: { tid: '' } }, status: 400 },
        { response: { code: 'RN002', message: '모집중인 공고가 아닙니다.', data: { tid: '' } }, status: 400 },
        { response: { code: 'RN002', message: '모집중인 공고가 아닙니다.', data: { tid: '' } }, status: 400 },
      ] satisfies MockResponseStep<TypeCreateWorkApplicationsResponse>[],
    },
  ],
});
